import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Paper
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Store as StoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { isWireTransferEnabled } from '../utils/featureFlags';

const PaymentStatusTracker = () => {
  const { t } = useTranslation(['dashboard', 'common']);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);

  useEffect(() => {
    fetchUserPayments();
  }, []);

  const fetchUserPayments = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/payment/my-payments');
      setPayments(response.data.data || []);
    } catch (err) {
      console.error('Error fetching user payments:', err);
      setError(err.response?.data?.error || 'Failed to fetch payment history');
    } finally {
      setLoading(false);
    }
  };

  const openPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setDetailsDialog(true);
  };

  const closePaymentDetails = () => {
    setSelectedPayment(null);
    setDetailsDialog(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      failed: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: ScheduleIcon,
      processing: ScheduleIcon,
      completed: CheckIcon,
      failed: ErrorIcon,
      cancelled: WarningIcon
    };
    const IconComponent = icons[status] || InfoIcon;
    return <IconComponent />;
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      credit_card: CreditCardIcon,
      wire_transfer: BankIcon,
      in_place: StoreIcon
    };
    const IconComponent = icons[method] || PaymentIcon;
    return <IconComponent />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getPaymentTypeLabel = (type) => {
    const labels = {
      membership: t('paymentStatus.types.membership'),
      class: t('paymentStatus.types.class'),
      next_plan: t('paymentStatus.types.nextPlan')
    };
    return labels[type] || type;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      credit_card: t('paymentStatus.methods.creditCard'),
      wire_transfer: t('paymentStatus.methods.wireTransfer'),
      in_place: t('paymentStatus.methods.inPlace')
    };
    return labels[method] || method;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('paymentStatus.statuses.pending'),
      processing: t('paymentStatus.statuses.processing'),
      completed: t('paymentStatus.statuses.completed'),
      failed: t('paymentStatus.statuses.failed'),
      cancelled: t('paymentStatus.statuses.cancelled')
    };
    return labels[status] || status;
  };

  const getPaymentSteps = (payment) => {
    const steps = [
      {
        label: t('paymentStatus.steps.initiated'),
        completed: true,
        active: false
      }
    ];

    if (payment.paymentMethod === 'credit_card') {
      steps.push({
        label: t('paymentStatus.steps.processing'),
        completed: payment.status !== 'pending',
        active: payment.status === 'processing'
      });
    } else if (isWireTransferEnabled() && payment.paymentMethod === 'wire_transfer') {
      steps.push({
        label: t('paymentStatus.steps.transferPending'),
        completed: payment.status === 'completed',
        active: payment.status === 'pending'
      });
    } else if (payment.paymentMethod === 'in_place') {
      steps.push({
        label: t('paymentStatus.steps.visitRequired'),
        completed: payment.status === 'completed',
        active: payment.status === 'pending'
      });
    }

    steps.push({
      label: t('paymentStatus.steps.completed'),
      completed: payment.status === 'completed',
      active: false
    });

    return steps;
  };

  const PaymentCard = ({ payment }) => (
    <Card sx={{ mb: 2, cursor: 'pointer' }} onClick={() => openPaymentDetails(payment)}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box display="flex" alignItems="center" mb={1}>
              {getPaymentMethodIcon(payment.paymentMethod)}
              <Typography variant="h6" sx={{ ml: 1 }}>
                {getPaymentTypeLabel(payment.paymentType)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {getPaymentMethodLabel(payment.paymentMethod)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Token: {payment.paymentToken}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="h5" color="primary">
              {formatCurrency(payment.amount)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(payment.createdAt)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Chip
              icon={getStatusIcon(payment.status)}
              label={getStatusLabel(payment.status)}
              color={getStatusColor(payment.status)}
              sx={{ mb: 1 }}
            />
            {payment.status === 'pending' && payment.expiresAt && (
              <Typography variant="caption" display="block" color="text.secondary">
                Expires: {formatDate(payment.expiresAt)}
              </Typography>
            )}
          </Grid>
        </Grid>

        {payment.paymentType === 'next_plan' && payment.nextPlanPurchase?.selectedPlan && (
          <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Next Plan:</strong> {payment.nextPlanPurchase.selectedPlan.name}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          {t('paymentStatus.title')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUserPayments}
          disabled={loading}
        >
          {t('common:buttons.refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {payments.length === 0 ? (
        <Alert severity="info">
          {t('paymentStatus.noPayments')}
        </Alert>
      ) : (
        <Box>
          {payments.map((payment) => (
            <PaymentCard key={payment._id} payment={payment} />
          ))}
        </Box>
      )}

      {/* Payment Details Dialog */}
      <Dialog open={detailsDialog} onClose={closePaymentDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('paymentStatus.details.title')}
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('paymentStatus.details.basicInfo')}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <PaymentIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('paymentStatus.details.type')}
                        secondary={getPaymentTypeLabel(selectedPayment.paymentType)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        {getPaymentMethodIcon(selectedPayment.paymentMethod)}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('paymentStatus.details.method')}
                        secondary={getPaymentMethodLabel(selectedPayment.paymentMethod)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ReceiptIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('paymentStatus.details.amount')}
                        secondary={formatCurrency(selectedPayment.amount)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        {getStatusIcon(selectedPayment.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('paymentStatus.details.status')}
                        secondary={getStatusLabel(selectedPayment.status)}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('paymentStatus.details.progress')}
                  </Typography>
                  <Stepper orientation="vertical">
                    {getPaymentSteps(selectedPayment).map((step, index) => (
                      <Step key={index} active={step.active} completed={step.completed}>
                        <StepLabel>{step.label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Payment Method Specific Information */}
              {isWireTransferEnabled() && selectedPayment.paymentMethod === 'wire_transfer' && selectedPayment.wireTransferDetails && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('paymentStatus.details.wireTransfer')}
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2">
                      <strong>{t('paymentStatus.details.bankName')}:</strong> {selectedPayment.wireTransferDetails.bankName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('paymentStatus.details.accountNumber')}:</strong> {selectedPayment.wireTransferDetails.accountNumber}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('paymentStatus.details.referenceCode')}:</strong> {selectedPayment.wireTransferDetails.referenceCode}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedPayment.paymentMethod === 'in_place' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('paymentStatus.details.inPlace')}
                  </Typography>
                  <Alert severity="info">
                    <Typography variant="body2">
                      {t('paymentStatus.details.inPlaceInstructions')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t('paymentStatus.details.paymentToken')}:</strong> {selectedPayment.paymentToken}
                    </Typography>
                  </Alert>
                </Box>
              )}

              {selectedPayment.attempts && selectedPayment.attempts.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('paymentStatus.details.history')}
                  </Typography>
                  <List dense>
                    {selectedPayment.attempts.map((attempt, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {attempt.status === 'success' ? <CheckIcon color="success" /> :
                           attempt.status === 'failed' ? <ErrorIcon color="error" /> :
                           <WarningIcon color="warning" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={`${attempt.status.charAt(0).toUpperCase() + attempt.status.slice(1)} attempt`}
                          secondary={formatDate(attempt.attemptedAt)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentDetails}>
            {t('common:buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentStatusTracker;